import React from 'react'
import { Message } from '../types'

interface MessageMetadataProps {
  message: Message
  onRevealNote: (recordName: string) => void
}

/**
 * Component for displaying message metadata such as edit proposals and referenced notes.
 * Shows additional information below the main message content.
 */
export function MessageMetadata({
  message,
  onRevealNote,
}: MessageMetadataProps) {
  return (
    <>
      {/* Show edit proposal indicator if present */}
      {message.proposedEdit && <EditProposalIndicator />}

      {/* Show referenced notes if present */}
      {message.referencedNotes && message.referencedNotes.length > 0 && (
        <ReferencedNotes
          notes={message.referencedNotes}
          onRevealNote={onRevealNote}
        />
      )}
    </>
  )
}

/**
 * Indicator shown when the AI has proposed an edit to the document.
 */
function EditProposalIndicator() {
  return (
    <div className='mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 dark:border-amber-800 dark:bg-amber-950'>
      <p className='text-xs font-medium text-amber-700 dark:text-amber-300'>
        ✏️ Edit proposed - Check the dialog above to review and apply changes
      </p>
    </div>
  )
}

interface ReferencedNotesProps {
  notes: Array<{ recordName: string; title: string; query?: string }>
  onRevealNote: (recordName: string) => void
}

/**
 * Component for displaying notes that were referenced during the AI's response.
 * Each note is a clickable button that reveals the note in the sidebar.
 * Notes are grouped by the search query that found them.
 */
function ReferencedNotes({ notes, onRevealNote }: ReferencedNotesProps) {
  // Deduplicate notes based on recordName within each query
  const uniqueNotesMap = new Map<string, (typeof notes)[0]>()

  notes.forEach((note) => {
    const key = `${note.recordName}-${note.query || 'General'}`
    if (!uniqueNotesMap.has(key)) {
      uniqueNotesMap.set(key, note)
    }
  })

  // Group deduplicated notes by search query
  const notesByQuery = Array.from(uniqueNotesMap.values()).reduce(
    (acc, note) => {
      const query = note.query || 'General'
      if (!acc[query]) {
        acc[query] = []
      }
      acc[query].push(note)
      return acc
    },
    {} as Record<string, typeof notes>
  )

  return (
    <div className='mb-3 mt-3 rounded-lg border border-amber-300 bg-amber-50/50 px-3 pb-1 pt-3 dark:border-amber-900 dark:bg-amber-950/30'>
      {/* Header section with total count */}
      <div className='-mx-3 -mt-3 mb-0 rounded-t-lg px-3 pb-0 pt-2 dark:bg-orange-800'>
        <p className='text-xs font-semibold text-amber-900/70 dark:text-amber-300'>
          REFERENCED NOTES ({uniqueNotesMap.size} TOTAL)
        </p>
      </div>

      {/* Group notes by search query */}
      {Object.entries(notesByQuery).map(([query, queryNotes]) => (
        <div key={query} className='mb-2'>
          {/* Search query label */}
          <p className='mb-1 pt-2 text-xs text-amber-800 dark:text-amber-500'>
            Search: &ldquo;{query}&rdquo;
          </p>

          {/* Grid of note buttons for this query */}
          <div className='flex flex-wrap justify-start gap-2'>
            {queryNotes.map((note, index) => {
              // Create unique key using both query and note record name
              const uniqueKey = `${query}-${note.recordName}-${index}`

              return (
                <button
                  key={uniqueKey}
                  onClick={() => onRevealNote(note.recordName)}
                  className='rounded-md bg-amber-100 px-2 py-1 text-left text-xs text-amber-800 hover:bg-amber-200 dark:bg-amber-900/50 dark:text-amber-300 dark:hover:bg-amber-900/70'
                >
                  {note.title}
                </button>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}
